import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";

import { useToast } from "hooks/toast";
import {
  consultarCnpj,
  criarEmbarcador,
  atualizarIdentidade,
} from "services/onboardingApi";
import { digitsOnly, formatDocument, formatCep, isValidCnpj } from "utils/formatDoc";

const CONTRIBUINTE_OPTIONS = [
  { value: 3, label: "Contribuinte ICMS" },
  { value: 2, label: "Não Contribuinte" },
  { value: 4, label: "Contribuinte Isento" },
];

const ESTABELECIMENTO_OPTIONS = [
  { value: 1, label: "Industrial" },
  { value: 2, label: "Comercial" },
  { value: 3, label: "Centro de Distribuição" },
];

const EMPTY = {
  cnpj: "",
  razaoSocial: "",
  fantasia: "",
  inscricaoEstadual: "",
  idContribuinteICMS: 3,
  idTipoEstabelecimento: 1,
  cep: "",
  logradouro: "",
  numero: "",
  complemento: "",
  bairro: "",
  cidade: "",
  uf: "",
};

// Identidade da matriz (PO1-2428). Mesmo formulário no criar (casca PENDENTE)
// e no editar — dados vêm da BrasilAPI e ficam editáveis.
export default function IdentidadeForm({ mode, embarcador, onSaved, onCancel }) {
  const { addToast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [buscando, setBuscando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [buscou, setBuscou] = useState(mode === "edit");
  // PO1-2659: só true quando o operador mexe no select — payload só manda
  // idContribuinteICMS nesse caso, para o backend aplicar o default (3)
  // quando o campo não vem tocado.
  const [contribuinteTocado, setContribuinteTocado] = useState(false);

  useEffect(() => {
    if (mode === "edit" && embarcador) {
      setForm({
        cnpj: embarcador.cnpjCpf || "",
        razaoSocial: embarcador.razaoSocial || "",
        fantasia: embarcador.fantasia || "",
        inscricaoEstadual: embarcador.inscricaoEstadual || "",
        // Valor real gravado no banco — não recalculado pela IE (a
        // heurística do front foi removida, PO1-2659).
        idContribuinteICMS: embarcador.idContribuinteICMS || 3,
        idTipoEstabelecimento: 1,
        cep: embarcador.cep || "",
        logradouro: embarcador.logradouro || "",
        numero: embarcador.numero || "",
        complemento: embarcador.complemento || "",
        bairro: embarcador.bairro || "",
        cidade: embarcador.cidade || "",
        uf: embarcador.uf || "",
      });
    }
  }, [mode, embarcador]);

  const setField = useCallback((campo, valor) => {
    if (campo === "idContribuinteICMS") {
      setContribuinteTocado(true);
    }
    setForm((prev) => ({ ...prev, [campo]: valor }));
  }, []);

  const handleBuscar = useCallback(async () => {
    const cnpj = digitsOnly(form.cnpj);
    if (!isValidCnpj(cnpj)) {
      addToast({
        place: "tr",
        type: "danger",
        title: "CNPJ inválido",
        description: "Confira o número digitado.",
        icon: "tim-icons icon-alert-circle-exc",
      });
      return;
    }
    setBuscando(true);
    try {
      const dados = await consultarCnpj(cnpj);
      setForm((prev) => ({
        ...prev,
        razaoSocial: dados.razaoSocial || prev.razaoSocial,
        fantasia: dados.fantasia || prev.fantasia,
        cep: dados.cep || prev.cep,
        logradouro: dados.logradouro || prev.logradouro,
        numero: dados.numero || prev.numero,
        complemento: dados.complemento || prev.complemento,
        bairro: dados.bairro || prev.bairro,
        cidade: dados.cidade || prev.cidade,
        uf: dados.uf || prev.uf,
      }));
      setBuscou(true);
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        err.message;
      addToast({
        place: "tr",
        type: "danger",
        title: "Falha na consulta do CNPJ",
        description: msg,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setBuscando(false);
    }
  }, [form.cnpj, addToast]);

  const handleSalvar = useCallback(async () => {
    if (!form.razaoSocial.trim()) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Razão Social é obrigatória",
        icon: "tim-icons icon-alert-circle-exc",
      });
      return;
    }
    if (digitsOnly(form.cep).length !== 8) {
      addToast({
        place: "tr",
        type: "danger",
        title: "CEP é obrigatório (8 dígitos)",
        icon: "tim-icons icon-alert-circle-exc",
      });
      return;
    }
    setSalvando(true);
    try {
      const payload = {
        cnpj: digitsOnly(form.cnpj),
        razaoSocial: form.razaoSocial,
        fantasia: form.fantasia,
        inscricaoEstadual: form.inscricaoEstadual,
        // PO1-2659: só manda o campo quando o operador mexeu no select;
        // caso contrário o backend aplica o default (dono único da regra).
        ...(contribuinteTocado
          ? { idContribuinteICMS: Number(form.idContribuinteICMS) }
          : {}),
        idTipoEstabelecimento: Number(form.idTipoEstabelecimento),
        cep: digitsOnly(form.cep),
        logradouro: form.logradouro,
        numero: form.numero,
        complemento: form.complemento,
        bairro: form.bairro,
      };
      const resultado =
        mode === "create"
          ? await criarEmbarcador(payload)
          : await atualizarIdentidade(embarcador.id, payload);
      addToast({
        place: "tr",
        type: "success",
        title:
          mode === "create"
            ? "Embarcador criado como PENDENTE"
            : "Identidade atualizada",
        icon: "tim-icons icon-check-2",
      });
      onSaved(resultado);
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        err.message;
      addToast({
        place: "tr",
        type: "danger",
        title: mode === "create" ? "Erro ao criar embarcador" : "Erro ao salvar",
        description: msg,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setSalvando(false);
    }
  }, [form, mode, embarcador, addToast, onSaved, contribuinteTocado]);

  return (
    <Form onSubmit={(e) => e.preventDefault()}>
      <Row>
        <Col md="5">
          <FormGroup>
            <Label>CNPJ</Label>
            <Input
              value={formatDocument(form.cnpj)}
              onChange={(e) => setField("cnpj", e.target.value)}
              disabled={mode === "edit"}
              placeholder="00.000.000/0000-00"
            />
          </FormGroup>
        </Col>
        {mode === "create" && (
          <Col md="3" className="d-flex align-items-end">
            <FormGroup>
              <Button
                color="primary"
                onClick={handleBuscar}
                disabled={buscando}
              >
                {buscando ? <Spinner size="sm" /> : "Buscar"}
              </Button>
            </FormGroup>
          </Col>
        )}
      </Row>
      {buscou && (
        <p style={{ fontSize: 12, color: "#00bf9a" }}>
          Dados preenchidos a partir do CNPJ (BrasilAPI) — revise e edite se
          necessário.
        </p>
      )}
      <Row>
        <Col md="7">
          <FormGroup>
            <Label>Razão Social</Label>
            <Input
              value={form.razaoSocial}
              onChange={(e) => setField("razaoSocial", e.target.value)}
            />
          </FormGroup>
        </Col>
        <Col md="5">
          <FormGroup>
            <Label>Fantasia</Label>
            <Input
              value={form.fantasia}
              onChange={(e) => setField("fantasia", e.target.value)}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormGroup>
            <Label>Inscrição Estadual</Label>
            <Input
              value={form.inscricaoEstadual}
              onChange={(e) => setField("inscricaoEstadual", e.target.value)}
              placeholder="número ou ISENTO"
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label>Contribuinte ICMS</Label>
            <Input
              type="select"
              value={form.idContribuinteICMS}
              onChange={(e) => setField("idContribuinteICMS", e.target.value)}
            >
              {CONTRIBUINTE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label>Tipo de Estabelecimento</Label>
            <Input
              type="select"
              value={form.idTipoEstabelecimento}
              onChange={(e) =>
                setField("idTipoEstabelecimento", e.target.value)
              }
            >
              {ESTABELECIMENTO_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
      </Row>
      <hr />
      <Row>
        <Col md="3">
          <FormGroup>
            <Label>CEP</Label>
            <Input
              value={formatCep(form.cep)}
              onChange={(e) => setField("cep", e.target.value)}
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label>Logradouro</Label>
            <Input
              value={form.logradouro}
              onChange={(e) => setField("logradouro", e.target.value)}
            />
          </FormGroup>
        </Col>
        <Col md="3">
          <FormGroup>
            <Label>Número</Label>
            <Input
              value={form.numero}
              onChange={(e) => setField("numero", e.target.value)}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormGroup>
            <Label>Bairro</Label>
            <Input
              value={form.bairro}
              onChange={(e) => setField("bairro", e.target.value)}
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label>Complemento</Label>
            <Input
              value={form.complemento}
              onChange={(e) => setField("complemento", e.target.value)}
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label>Cidade / UF</Label>
            <Input
              value={
                form.cidade ? `${form.cidade}${form.uf ? ` / ${form.uf}` : ""}` : ""
              }
              disabled
            />
          </FormGroup>
        </Col>
      </Row>
      <div className="d-flex justify-content-between mt-3">
        <Button color="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button color="primary" onClick={handleSalvar} disabled={salvando}>
          {salvando ? <Spinner size="sm" /> : "Salvar"}
        </Button>
      </div>
    </Form>
  );
}
